import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Users,
  Target,
  MessageSquare,
  Edit,
  Loader2,
  Shield,
  User,
  HelpCircle,
  Send,
  Clock,
  CheckCircle2,
  Sparkles,
  KeyRound,
  Mail,
  Copy
} from "lucide-react";
import * as LucideIcons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [objectionQuestions, setObjectionQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [replyText, setReplyText] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [generatedPassword, setGeneratedPassword] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, techRes, questionsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/techniques`),
        axios.get(`${API}/admin/objections/questions`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTechniques(techRes.data);
      setObjectionQuestions(questionsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTechnique = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/techniques/${selectedTechnique.id}`, editFormData);
      toast.success("Technique updated!");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update technique");
    } finally {
      setSaving(false);
    }
  };

  const handleReplyToQuestion = async () => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/objections/questions/${selectedQuestion.id}/reply`, {
        reply: replyText
      });
      toast.success("Reply sent!");
      setReplyDialogOpen(false);
      setSelectedQuestion(null);
      setReplyText("");
      fetchData();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`);
      toast.success("Role updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
        send_email: sendEmail
      });
      
      if (sendEmail && response.data.email_sent) {
        toast.success(`Password reset! Email sent to ${selectedUser.email}`);
      } else if (!sendEmail) {
        setGeneratedPassword(newPassword);
        toast.success("Password reset! Copy the password below.");
      } else {
        setGeneratedPassword(newPassword);
        toast.warning("Password reset but email failed to send. Copy the password below.");
      }
      
      if (sendEmail) {
        setResetPasswordDialogOpen(false);
        setSelectedUser(null);
        setNewPassword("");
      }
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getTechniqueIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? (
      <IconComponent className="w-5 h-5" />
    ) : (
      <MessageSquare className="w-5 h-5" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-2">
          <Shield className="w-8 h-8 text-gba-green" />
          Admin Panel
        </h1>
        <p className="text-slate-400 mt-1">
          Manage users and follow-up techniques
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1E293B] border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Users</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.total_users || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-900/50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Prospects</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.total_prospects || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-900/50 flex items-center justify-center">
                <Target className="w-6 h-6 text-gba-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Follow-ups</p>
                <p className="font-heading text-3xl font-bold text-white mt-1">
                  {stats?.total_followups || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-900/50 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#1E293B] border border-slate-700 p-1">
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="admin-users-tab"
          >
            Users ({users.length})
          </TabsTrigger>
          <TabsTrigger
            value="techniques"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="admin-techniques-tab"
          >
            Techniques (32)
          </TabsTrigger>
          <TabsTrigger
            value="objections"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="admin-objections-tab"
          >
            Questions ({objectionQuestions.filter(q => q.status !== "answered").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Streak</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.id}
                      className="border-slate-700"
                      data-testid={`admin-user-row-${user.id}`}
                    >
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gba-green/20 flex items-center justify-center text-gba-green">
                            {user.name?.charAt(0).toUpperCase() || "U"}
                          </div>
                          {user.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-400">{user.email}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            user.role === "admin"
                              ? "bg-amber-900/50 text-amber-400"
                              : "bg-slate-700 text-slate-300"
                          }`}
                        >
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-white">
                        {user.streak || 0} 🔥
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setNewPassword("");
                            setGeneratedPassword("");
                            setResetPasswordDialogOpen(true);
                          }}
                          className="text-blue-400 hover:text-blue-300"
                          data-testid={`reset-password-${user.id}`}
                        >
                          <KeyRound className="w-4 h-4 mr-1" />
                          Reset Password
                        </Button>
                        {user.role === "user" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdateRole(user.id, "admin")}
                            className="text-amber-400 hover:text-amber-300"
                          >
                            Make Admin
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUpdateRole(user.id, "user")}
                            className="text-slate-400 hover:text-slate-300"
                          >
                            Remove Admin
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="techniques" className="mt-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400 w-16">#</TableHead>
                    <TableHead className="text-slate-400">Technique</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {techniques.map((technique) => (
                    <TableRow
                      key={technique.id}
                      className="border-slate-700"
                      data-testid={`admin-technique-row-${technique.id}`}
                    >
                      <TableCell className="text-slate-500 font-mono">
                        {technique.order}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gba-green/20 flex items-center justify-center text-gba-green">
                            {getTechniqueIcon(technique.icon)}
                          </div>
                          <div>
                            <p className="text-white font-medium">{technique.name}</p>
                            <p className="text-slate-500 text-xs line-clamp-1">
                              {technique.description}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-400 text-sm">
                          {technique.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTechnique(technique);
                            setEditFormData({
                              name: technique.name,
                              description: technique.description,
                              icon: technique.icon,
                              email_template: technique.email_template,
                              whatsapp_template: technique.whatsapp_template,
                              category: technique.category,
                            });
                            setEditDialogOpen(true);
                          }}
                          className="text-slate-400 hover:text-white"
                          data-testid={`edit-technique-${technique.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="objections" className="mt-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-amber-400" />
                User Objection Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {objectionQuestions.length === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No questions submitted yet</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-4">
                    {objectionQuestions.map((question) => (
                      <div
                        key={question.id}
                        className={`p-4 rounded-lg border ${
                          question.status === "answered"
                            ? "bg-gba-green/10 border-gba-green/30"
                            : question.status === "ai_suggested"
                            ? "bg-purple-900/20 border-purple-800/50"
                            : "bg-amber-900/20 border-amber-800/50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-medium text-white">
                                {question.user_name}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                question.status === "answered"
                                  ? "bg-gba-green/20 text-gba-green"
                                  : question.status === "ai_suggested"
                                  ? "bg-purple-900/30 text-purple-400"
                                  : "bg-amber-900/30 text-amber-400"
                              }`}>
                                {question.status === "answered" ? (
                                  <span className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> Answered
                                  </span>
                                ) : question.status === "ai_suggested" ? (
                                  <span className="flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" /> AI Suggested
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> Pending
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(question.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-white mb-2">"{question.objection_text}"</p>
                            {question.context && (
                              <p className="text-sm text-slate-500 mb-2">
                                Context: {question.context}
                              </p>
                            )}
                            
                            {question.ai_suggestion && (
                              <div className="bg-slate-800/50 rounded-lg p-3 mb-2">
                                <p className="text-xs text-purple-400 mb-1 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> AI Suggestion:
                                </p>
                                <p className="text-sm text-slate-300 whitespace-pre-wrap">
                                  {question.ai_suggestion.substring(0, 200)}...
                                </p>
                              </div>
                            )}
                            
                            {question.admin_reply && (
                              <div className="bg-gba-green/10 rounded-lg p-3">
                                <p className="text-xs text-gba-green mb-1">Your Reply:</p>
                                <p className="text-sm text-slate-300">{question.admin_reply}</p>
                              </div>
                            )}
                          </div>
                          
                          {question.status !== "answered" && (
                            <Button
                              onClick={() => {
                                setSelectedQuestion(question);
                                setReplyText(question.ai_suggestion || "");
                                setReplyDialogOpen(true);
                              }}
                              className="bg-gba-green hover:bg-gba-green-hover flex-shrink-0"
                              data-testid={`reply-question-${question.id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Reply
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Technique Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              Edit Technique
            </DialogTitle>
          </DialogHeader>
          {selectedTechnique && (
            <form onSubmit={handleUpdateTechnique} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Name</Label>
                  <Input
                    value={editFormData.name || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                    className="bg-[#0F172A] border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Icon (Lucide name)</Label>
                  <Input
                    value={editFormData.icon || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, icon: e.target.value })
                    }
                    placeholder="e.g., Phone, Mail, Gift"
                    className="bg-[#0F172A] border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <textarea
                  value={editFormData.description || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  className="w-full h-20 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Input
                  value={editFormData.category || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, category: e.target.value })
                  }
                  className="bg-[#0F172A] border-slate-700 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Email Template</Label>
                <textarea
                  value={editFormData.email_template || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, email_template: e.target.value })
                  }
                  className="w-full h-32 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white resize-none font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">WhatsApp Template</Label>
                <textarea
                  value={editFormData.whatsapp_template || ""}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      whatsapp_template: e.target.value,
                    })
                  }
                  className="w-full h-24 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white resize-none font-mono text-sm"
                />
              </div>

              <p className="text-xs text-slate-500">
                Use {"{name}"} as a placeholder for the prospect's name
              </p>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 uppercase tracking-wide"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply to Question Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white flex items-center gap-2">
              <Send className="w-6 h-6 text-gba-green" />
              Reply to Question
            </DialogTitle>
          </DialogHeader>
          {selectedQuestion && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-1">Question from {selectedQuestion.user_name}:</p>
                <p className="text-white">"{selectedQuestion.objection_text}"</p>
                {selectedQuestion.context && (
                  <p className="text-sm text-slate-400 mt-2">Context: {selectedQuestion.context}</p>
                )}
              </div>

              {selectedQuestion.ai_suggestion && (
                <div className="bg-purple-900/20 border border-purple-800/50 rounded-lg p-3">
                  <p className="text-xs text-purple-400 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI Suggestion (you can edit):
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">Your Reply</Label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Provide your expert guidance..."
                  className="w-full h-40 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white placeholder:text-slate-600 resize-none"
                  data-testid="reply-text-input"
                />
              </div>

              <Button
                onClick={handleReplyToQuestion}
                disabled={saving || !replyText.trim()}
                className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 uppercase tracking-wide"
                data-testid="send-reply-button"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Reply
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => {
        setResetPasswordDialogOpen(open);
        if (!open) {
          setSelectedUser(null);
          setNewPassword("");
          setGeneratedPassword("");
        }
      }}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-gba-green" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-slate-400">Resetting password for:</p>
                <p className="text-white font-medium">{selectedUser.name}</p>
                <p className="text-slate-400 text-sm">{selectedUser.email}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">New Password</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="flex-1 bg-[#0F172A] border-slate-700 text-white"
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateRandomPassword}
                    className="border-slate-600 hover:bg-slate-700"
                  >
                    Generate
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-gba-green"
                />
                <Label htmlFor="sendEmail" className="text-slate-300 cursor-pointer">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Send new password via email
                </Label>
              </div>

              {generatedPassword && (
                <div className="bg-gba-green/10 border border-gba-green/30 rounded-lg p-4">
                  <p className="text-sm text-gba-green mb-2">Password has been reset:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-900 px-3 py-2 rounded text-white font-mono">
                      {generatedPassword}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(generatedPassword)}
                      className="text-gba-green hover:text-gba-green"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Make sure to share this password securely with the user.
                  </p>
                </div>
              )}

              <Button
                onClick={handleResetPassword}
                disabled={saving || !newPassword}
                className="w-full bg-gba-green hover:bg-gba-green-hover"
                data-testid="confirm-reset-button"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
